import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { TIMEZONES } from "constants/timezones"
import { SLOT_LABEL_FORMAT } from "../../../constants/schedulingConstants"
import { REPEAT_TYPES } from "./constants"

dayjs.extend(utc)
dayjs.extend(timezone)

export const generateId = () => `${Date.now()}_${Math.random()}`

export const defaultTimeRow = () => ({ id: generateId(), time: null })

export const formatSlotLabel = (date, time) =>
  `${date.format("ddd MMM D, YYYY")} ${time.format("h:mm A")}`

// The listing's time zone when it's a real IANA zone, else undefined (the browser's zone is used).
export const resolveTimeZone = (timeZone) => {
  if (!timeZone) return undefined
  try {
    return Intl.DateTimeFormat("en-US", { timeZone }).resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

// "(UTC-5) Eastern Time" for the zones the profile's Timezone picker offers, else the zone's name.
export const getTimeZoneLabel = (timeZone) => TIMEZONES.find((zone) => zone.value === timeZone)?.label ?? timeZone

// A start time's label ("Mon Nov 2, 2026 9:00 AM") on the listing's clock.
export const formatSlotLabelInZone = (startsAt, timeZone) => {
  const zone = resolveTimeZone(timeZone)
  return (zone ? dayjs(startsAt).tz(zone) : dayjs(startsAt)).format(SLOT_LABEL_FORMAT)
}

// The instant of `time` on `date` on the listing's clock, so "9:00 AM" means 9:00 AM where the
// experience runs whatever zone the guide's computer is in.
const toStartsAt = (date, time, timeZone) => {
  const zone = resolveTimeZone(timeZone)
  if (!zone) return date.clone().hour(time.hour()).minute(time.minute()).second(0).toISOString()
  return dayjs.tz(`${date.format("YYYY-MM-DD")} ${time.format("HH:mm")}`, zone).toISOString()
}

export const getDayIndicesForType = (repeatType) => {
  if (repeatType === REPEAT_TYPES.EVERYDAY) return [0, 1, 2, 3, 4, 5, 6]
  if (repeatType === REPEAT_TYPES.WEEKDAYS) return [1, 2, 3, 4, 5]
  if (repeatType === REPEAT_TYPES.WEEKEND) return [0, 6]
  return []
}

export const buildSlots = ({ startDate, endDate, timeRows, isRepeat, repeatType, customDays, timeZone }) => {
  const activeDays = repeatType === REPEAT_TYPES.CUSTOM ? customDays : getDayIndicesForType(repeatType)
  const slots = []
  let current = startDate.clone()

  while (current.isBefore(endDate, "day") || current.isSame(endDate, "day")) {
    const include = !isRepeat || activeDays.includes(current.day())
    if (include) {
      timeRows.forEach((row) => {
        // starts_at is stored as ISO string so the payload builder can send it without reparsing the label
        slots.push({ id: generateId(), label: formatSlotLabel(current, row.time), starts_at: toStartsAt(current, row.time, timeZone) })
      })
    }
    current = current.add(1, "day")
  }

  return slots
}

/**
 * Pairs of departures that run at the same time: a departure that starts before the previous one
 * (in start order) has ended. Checking neighbours in start order is enough to find every overlap.
 * Used to warn — not block — when a multi-day schedule has groups out at the same time.
 */
export const findOverlappingDepartures = (slots, durationMinutes) => {
  if (!durationMinutes) return []
  const sorted = slots
    .filter((slot) => slot.starts_at && !slot._destroy)
    .map((slot) => ({ ...slot, start: dayjs(slot.starts_at) }))
    .sort((first, second) => first.start.valueOf() - second.start.valueOf())
  return sorted.reduce((overlaps, slot, index) => {
    const next = sorted[index + 1]
    if (next && next.start.isBefore(slot.start.add(durationMinutes, "minute"))) overlaps.push([slot, next])
    return overlaps
  }, [])
}
